import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { Swiper, SwiperSlide } from 'swiper/react';
import React, {
    useEffect,
    useRef,
    useState,
    createContext,
    useContext,
    useMemo,
} from 'react';

import { useKeyboard } from '$hooks/events.js';
import { Button } from '$partials/ui';
import { IconChevronLeft, IconChevronRight, IconClose } from '$partials/icon';
import { GalleryVideo, Media } from '$partials/media';

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/** @type {React.Context<import('$types/components/partials/media.d.ts').MediaGalleryContext>} */

const MediaGalleryContext = createContext({
    initId: null,
    setInitId: () => {}
});

/**
 * @hook useMediaGallery
 */
/**
 * @type {import('$types/components/partials/media.d.ts').useMediaGallery}
 */

const useMediaGallery = () => {
    const context = useContext(MediaGalleryContext);

    if (context === undefined) {
        throw new Error('useMediaGallery must be used within a MediaGalleryProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/media.d.ts').MediaGalleryProvider}
 */
const MediaGalleryProvider = ({ children }) => {
    const [initId, setInitId] = useState(null);
    const [videoId, setVideoId] = useState(null);

    return (
        <MediaGalleryContext.Provider value={useMemo(() => {
            return {
                initId,
                setInitId,
                setVideoId,
                videoId
            };
        }, [
            initId,
            setInitId,
            setVideoId,
            videoId
        ])}>
            {children}
        </MediaGalleryContext.Provider>
    );
};

/**
 * @type {import('$types/components/partials/media.d.ts').MediaGallery}
 */

const MediaGallery = ({ items, initialId }) => {
    const [swiper, setSwiper] = useState();
    const initialIndex = useMemo(() => {
        if (initialId === null || initialId === undefined) {return 0;}
        const index = items?.findIndex(item => item.id === initialId);
        return index >= 0 ? index : 0;
    }, [initialId, items]);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const dialogRef = useRef();
    const scrollElementRef = useRef();
    const ctx = useMediaGallery();

    useKeyboard({
        'Escape': (event) => {
            // Prevent dialog's default behaviour
            event.preventDefault();
            ctx.setInitId(null);
        },
        'ArrowLeft': () => {
            swiper?.slidePrev?.(0);
            ctx.setVideoId(null);
        },
        'ArrowRight': () => {
            swiper?.slideNext?.(0);
            ctx.setVideoId(null);
        }
    }, []);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        const scrollElementNode = scrollElementRef.current;
        if (dialogNode && !dialogNode.open && scrollElementNode) {
            enableBodyScroll(scrollElementNode);
            dialogNode.showModal?.();
        }
        else {
            disableBodyScroll(scrollElementNode);
            dialogNode.close?.();
        }

        return () => {
            dialogNode.close?.();
            enableBodyScroll(scrollElementNode);
        };
    }, []);

    const renderedItems = items?.filter(itm => (itm?.image || itm?.video || itm?.videoEmbed));

    useEffect(() => {
        // Sync currentIndex when initialIndex changes
        if (initialIndex !== null && initialIndex !== undefined) {
            setCurrentIndex(initialIndex);
        }
    }, [initialIndex]);

    return (
        <>
            <dialog
                className='dialog-reset'
                ref={dialogRef}>
                <div
                    className={'w-full fixed top-0 bottom-0 left-0 h-dvh max-h-dvh bg-gallery-black'}
                    ref={scrollElementRef}>

                    <div className={'flex justify-end absolute z-20 bottom-8 sm:bottom-auto gap-x-4 right-8 sm:top-24 sm:right-24'}>
                        <Button
                            className='flex select-none h-[40px] w-[40px] rounded justify-center items-center text-text-default-invert hover:bg-gallery-hover/10'
                            onClick={() => {
                                swiper?.slidePrev?.(0);
                                ctx.setVideoId(null);
                            }}>
                            <IconChevronLeft/>
                        </Button>
                        <Button
                            className='flex select-none h-[40px] w-[40px] rounded justify-center items-center text-text-default-invert hover:bg-gallery-hover/10'
                            onClick={() => {
                                swiper?.slideNext?.(0);
                                ctx.setVideoId(null);
                            }}>
                            <IconChevronRight/>
                        </Button>
                        <Button
                            className='flex select-none h-[40px] w-[40px] rounded justify-center items-center text-text-default-invert hover:bg-gallery-hover/10'
                            onClick={() => {
                                ctx.setInitId(null);
                                ctx.setVideoId(null);
                            }}
                            tabIndex={0}>
                            <IconClose/>
                        </Button>
                    </div>

                    {/*  Mobile only navigation */}
                    <div
                        className='sm:hidden absolute top-0 h-[calc(100dvh-48px)] left-0 w-48 z-10'
                        onClick={() => {
                            swiper?.slidePrev?.(0);
                            ctx.setVideoId(null);
                        }}/>
                    <div
                        className='sm:hidden absolute top-0 h-[calc(100dvh-48px)] right-0 w-48 z-10'
                        onClick={() => {
                            swiper?.slideNext?.(0);
                            ctx.setVideoId(null);
                        }}/>

                    <Swiper
                        allowTouchMove={false}
                        className='overflow-auto h-full'
                        grabCursor={false}
                        initialSlide={initialIndex}
                        onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
                        onSwiper={setSwiper}
                        simulateTouch={false}
                        slidesPerView={1}>
                        {renderedItems.map((item, idx) => {
                            const video = item.video;
                            const videoEmbed = item.videoEmbed;
                            const hasVideo = !!video?.src || !!videoEmbed?.src;
                            const isEmbed = !!videoEmbed?.src;
                            return (
                                <SwiperSlide
                                    aria-roledescription='slide'
                                    className='h-full'
                                    key={idx}
                                    role='group'>
                                    {hasVideo ? (
                                        <div className='h-full w-full flex justify-center items-center relative'>
                                            {isEmbed ? (
                                                <GalleryVideo
                                                    {...{
                                                        ...item.videoEmbed,
                                                        id: item.id,
                                                        poster: videoEmbed?.poster || videoEmbed?.embed?.thumbnailUrl,
                                                    }}/>
                                            ) : (
                                                <GalleryVideo
                                                    {...{
                                                        ...item.video,
                                                        id: item.id,
                                                    }}/>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className='w-auto h-dvh sm:hidden overflow-scroll'>
                                                <Media
                                                    image={{
                                                        ...item.image,
                                                        containerStyle: {
                                                            overflow: 'scroll',
                                                            paddingBottom: '100vh'
                                                        },
                                                        style: {
                                                            maxWidth: 'none',
                                                            height: '100%',
                                                            width: 'auto'
                                                        },
                                                        setToCenter: currentIndex === idx
                                                    }}/>
                                            </div>
                                            <div className='h-full w-full hidden sm:flex overflow-scroll'>
                                                <Media
                                                    image={{
                                                        ...item.image,
                                                    }}/>
                                            </div>
                                        </>
                                    )}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            </dialog>
        </>
    );
};

export default MediaGallery;

export {
    MediaGalleryProvider,
    MediaGalleryContext,
    useMediaGallery
};
